import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Trash2, X, Pencil, Globe, MapPin, Target,
  Megaphone, Rocket, Search, TrendingUp, AlertTriangle, ChevronDown,
  Pin, PinOff, ArrowUp, ArrowDown
} from "lucide-react";
import type { Accelerator } from "@shared/schema";

const INVESTMENT_STAGES = ["Angel", "Seed", "Series A", "Series B+"];
/** Single site-owner identity allowed to curate pinned programs. Mirror of server-side SITE_OWNER_EMAIL. */
const SITE_OWNER_EMAIL = "matt@learnpdi.com";

type SortField = "firmName" | "hqLocation" | "investmentFocus" | "recentAnnouncements";

export default function Accelerators() {
  const { user } = useAuth();
  const isOwner = ["matt@mattrebeiro.com", "matt@learnpdi.com"].includes(user?.email || "");
  const isSiteOwner = (user?.email || "").toLowerCase() === SITE_OWNER_EMAIL;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<Accelerator | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortField>("firmName");
  const [filterHq, setFilterHq] = useState("");
  const [filterFocus, setFilterFocus] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const [form, setForm] = useState({
    firmName: "",
    website: "",
    hqLocation: "",
    investmentStage: "",
    investmentFocus: "",
    recentAnnouncements: "",
  });

  const { data: items, isLoading } = useQuery<Accelerator[]>({
    queryKey: ["/api/accelerators"],
    queryFn: async () => {
      // `cache: no-cache` ensures owner pin/reorder changes show immediately without waiting
      // for the public Cache-Control: max-age=60 to expire on the GET response.
      const res = await fetch("/api/accelerators", { credentials: "include", cache: "no-cache" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const res = await fetch("/api/accelerators", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to create");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accelerators"] });
      resetForm();
      toast({ title: "Program added to directory" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: typeof form }) => {
      const res = await fetch(`/api/accelerators/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to update");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accelerators"] });
      resetForm();
      toast({ title: "Program updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/accelerators/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accelerators"] });
      toast({ title: "Program removed from directory" });
    },
  });

  const pinMutation = useMutation({
    mutationFn: async ({ id, pinned }: { id: number; pinned: boolean }) => {
      const res = await fetch(`/api/accelerators/${id}/pin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinned }),
        credentials: "include",
      });
      if (!res.ok) throw new Error(pinned ? "Failed to pin" : "Failed to unpin");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/accelerators"] });
      toast({ title: vars.pinned ? "Pinned to top of directory" : "Unpinned" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const res = await fetch(`/api/accelerators/pins/order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to reorder");
      return res.json();
    },
    // Optimistic local reorder for snappy arrow-button feedback; cache invalidation refetches the
    // canonical order on success/error.
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["/api/accelerators"] });
      const prev = queryClient.getQueryData<Accelerator[]>(["/api/accelerators"]);
      if (prev) {
        const orderMap = new Map(ids.map((id, idx) => [id, idx + 1]));
        const next = prev.map((a) => (orderMap.has(a.id) ? { ...a, pinOrder: orderMap.get(a.id)! } : a));
        queryClient.setQueryData(["/api/accelerators"], next);
      }
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["/api/accelerators"], ctx.prev);
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accelerators"] });
    },
  });

  function resetForm() {
    setForm({ firmName: "", website: "", hqLocation: "", investmentStage: "", investmentFocus: "", recentAnnouncements: "" });
    setEditingItem(null);
    setShowForm(false);
  }

  function startEdit(item: Accelerator) {
    setEditingItem(item);
    setForm({
      firmName: item.firmName,
      website: item.website,
      hqLocation: item.hqLocation,
      investmentStage: item.investmentStage,
      investmentFocus: item.investmentFocus,
      recentAnnouncements: item.recentAnnouncements,
    });
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  }

  const uniqueHqs = Array.from(new Set((items || []).map((f) => f.hqLocation).filter(Boolean))).sort();
  const uniqueFocuses = Array.from(new Set((items || []).map((f) => f.investmentFocus).filter(Boolean))).sort();

  // Pinned items always lead the (already filtered) list in pinOrder ascending; everything else
  // follows the user's chosen sortBy. Pinned items still respect filters/search — non-matching
  // pins are simply hidden, same as any other row.
  const filtered = (items || [])
    .filter((f) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q ||
        f.firmName.toLowerCase().includes(q) ||
        f.hqLocation.toLowerCase().includes(q) ||
        f.investmentFocus.toLowerCase().includes(q) ||
        f.recentAnnouncements.toLowerCase().includes(q);
      const matchesHq = !filterHq || f.hqLocation === filterHq;
      const matchesFocus = !filterFocus || f.investmentFocus === filterFocus;
      const matchesStage = !filterStage || f.investmentStage === filterStage;
      return matchesSearch && matchesHq && matchesFocus && matchesStage;
    })
    .sort((a, b) => {
      const aPinned = a.pinOrder != null;
      const bPinned = b.pinOrder != null;
      if (aPinned && bPinned) return (a.pinOrder! - b.pinOrder!);
      if (aPinned !== bPinned) return aPinned ? -1 : 1;
      const valA = (a[sortBy] || "").toLowerCase();
      const valB = (b[sortBy] || "").toLowerCase();
      return valA.localeCompare(valB);
    });

  // The full pinned ordering (across all items, regardless of current filter) is what the
  // reorder endpoint expects — moving a pinned card visually within filtered results must not
  // drop hidden pinned items from the canonical pinOrder.
  const pinnedOrdered = (items || [])
    .filter((a) => a.pinOrder != null)
    .sort((a, b) => (a.pinOrder! - b.pinOrder!));

  function movePin(id: number, delta: -1 | 1) {
    const idx = pinnedOrdered.findIndex((a) => a.id === id);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= pinnedOrdered.length) return;
    const next = pinnedOrdered.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    reorderMutation.mutate(next.map((a) => a.id));
  }

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Directory</span>
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-6" data-testid="text-accelerators-title">Accelerator & Incubator Directory</h1>
                  <p className="text-xl text-muted-foreground max-w-2xl mb-4">
                    Discover accelerator and incubator programs that support founders through mentorship, funding, and structured startup development.
                  </p>
                  <button
                    onClick={() => setDisclaimerOpen(!disclaimerOpen)}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="button-accelerator-disclaimer"
                    aria-expanded={disclaimerOpen}
                    aria-controls="accelerator-disclaimer-content"
                  >
                    <AlertTriangle size={12} aria-hidden="true" />
                    <span className="font-medium">Disclaimer</span>
                    <ChevronDown size={12} className={`transition-transform duration-200 ${disclaimerOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  {disclaimerOpen && (
                    <p id="accelerator-disclaimer-content" className="mt-3 text-xs text-muted-foreground leading-relaxed max-w-2xl" role="note">
                      Information in this directory is provided for general informational purposes only. While reasonable efforts are made to keep listings current, we make no representations or warranties regarding the completeness, accuracy, or reliability of any program details, including eligibility requirements, benefits, credit amounts, or availability. Program terms are determined solely by the respective providers and may change at any time. Inclusion of a program does not constitute an endorsement or guarantee of acceptance. We do not independently verify listings and assume no obligation to update them. Users should verify all program details directly with the provider before relying on this information. All program names, trademarks, and logos are the property of their respective owners and are used solely for identification purposes.
                    </p>
                  )}
                </div>
                {isOwner && (
                  <Button onClick={() => { resetForm(); setShowForm(true); }} className="shrink-0" data-testid="button-add-accelerator">
                    <Plus size={16} className="mr-2" /> Add Program
                  </Button>
                )}
              </div>
            </div>

            {showForm && isOwner && (
              <Card className="border-white/10 p-6 mb-10">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">{editingItem ? "Edit Program" : "Add New Program"}</h3>
                  <button onClick={resetForm} aria-label="Close form" className="p-3 -m-2 rounded-md hover:bg-white/5 transition-colors"><X size={18} className="text-muted-foreground" aria-hidden="true" /></button>
                </div>
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="acc-firm-name">Firm Name</Label>
                      <Input
                        id="acc-firm-name"
                        value={form.firmName}
                        onChange={(e) => setForm({ ...form, firmName: e.target.value })}
                        placeholder="e.g. Y Combinator"
                        className="bg-background border-white/10"
                        required
                        data-testid="input-accelerator-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="acc-website">Website</Label>
                      <Input
                        id="acc-website"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        placeholder="e.g. ycombinator.com"
                        className="bg-background border-white/10"
                        data-testid="input-accelerator-website"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="acc-hq">HQ Location</Label>
                      <Input
                        id="acc-hq"
                        value={form.hqLocation}
                        onChange={(e) => setForm({ ...form, hqLocation: e.target.value })}
                        placeholder="e.g. San Francisco, CA"
                        className="bg-background border-white/10"
                        data-testid="input-accelerator-hq"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="acc-stage">Investment Stage</Label>
                      <select
                        id="acc-stage"
                        value={form.investmentStage}
                        onChange={(e) => setForm({ ...form, investmentStage: e.target.value })}
                        className="w-full bg-background border border-white/10 rounded-md px-3 py-2.5 text-sm min-h-[44px]"
                        data-testid="input-accelerator-stage"
                      >
                        <option value="">Select stage...</option>
                        {INVESTMENT_STAGES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="acc-focus">Investment Focus</Label>
                      <Input
                        id="acc-focus"
                        value={form.investmentFocus}
                        onChange={(e) => setForm({ ...form, investmentFocus: e.target.value })}
                        placeholder="e.g. Pre-Seed, Diverse Sectors"
                        className="bg-background border-white/10"
                        data-testid="input-accelerator-focus"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="acc-notes">Notes</Label>
                    <Textarea
                      id="acc-notes"
                      value={form.recentAnnouncements}
                      onChange={(e) => setForm({ ...form, recentAnnouncements: e.target.value })}
                      placeholder="Latest cohorts, notable alumni, program updates, etc."
                      rows={4}
                      className="bg-background border-white/10"
                      data-testid="input-accelerator-announcements"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      data-testid="button-submit-accelerator"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? "Saving..."
                        : editingItem
                          ? "Update Program"
                          : "Add Program"}
                    </Button>
                    <Button type="button" variant="outline" className="border-white/10" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            <div className="flex flex-col md:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search programs..."
                  aria-label="Search accelerator programs"
                  className="bg-card border-white/10 pl-10"
                  data-testid="input-search-accelerators"
                />
              </div>
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                aria-label="Filter by investment stage"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[160px] min-h-[44px]"
                data-testid="select-filter-acc-stage"
              >
                <option value="">All Stages</option>
                {INVESTMENT_STAGES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={filterHq}
                onChange={(e) => setFilterHq(e.target.value)}
                aria-label="Filter by HQ location"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[180px] min-h-[44px]"
                data-testid="select-filter-acc-hq"
              >
                <option value="">All Locations</option>
                {uniqueHqs.map((hq) => (
                  <option key={hq} value={hq}>{hq}</option>
                ))}
              </select>
              <select
                value={filterFocus}
                onChange={(e) => setFilterFocus(e.target.value)}
                aria-label="Filter by focus area"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[180px] min-h-[44px]"
                data-testid="select-filter-acc-focus"
              >
                <option value="">All Focus Areas</option>
                {uniqueFocuses.map((focus) => (
                  <option key={focus} value={focus}>{focus}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortField)}
                aria-label="Sort results"
                className="bg-card border border-white/10 rounded-md px-3 py-2.5 text-sm min-w-[180px] min-h-[44px]"
                data-testid="select-sort-acc"
              >
                <option value="firmName">Sort: Name (A-Z)</option>
                <option value="hqLocation">Sort: HQ Location</option>
                <option value="investmentFocus">Sort: Focus Area</option>
                <option value="recentAnnouncements">Sort: Announcements</option>
              </select>
            </div>

            <div aria-live="polite" role="status" className="sr-only">
              {!isLoading && items ? `${filtered.length} of ${items.length} programs shown` : "Loading accelerator directory"}
            </div>

            {isLoading ? (
              <div className="space-y-4" aria-busy="true" aria-label="Loading accelerator programs">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-card border border-white/5 rounded-2xl p-8 animate-pulse">
                    <div className="h-6 bg-white/5 rounded w-1/3 mb-4" />
                    <div className="h-4 bg-white/5 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : filtered.length > 0 ? (
              <div className="space-y-4" role="list" aria-label="Accelerator programs">
                {filtered.map((item) => {
                  const isPinned = item.pinOrder != null;
                  const pinnedIdx = isPinned ? pinnedOrdered.findIndex((p) => p.id === item.id) : -1;
                  const isFirstPin = isPinned && pinnedIdx === 0;
                  const isLastPin = isPinned && pinnedIdx === pinnedOrdered.length - 1;
                  return (
                  <Card
                    role="listitem"
                    key={item.id}
                    className={`border-white/5 p-6 md:p-8 hover:border-primary/20 transition-all ${isPinned ? "border-primary/20 bg-primary/[0.02]" : ""}`}
                    data-testid={`card-accelerator-${item.id}`}
                    data-pinned={isPinned ? "true" : "false"}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <Rocket size={20} className="text-primary shrink-0" aria-hidden="true" />
                          <h2 className="text-xl font-display font-bold">{item.firmName}</h2>
                          {isPinned && (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary font-medium inline-flex items-center gap-1"
                              data-testid={`badge-pinned-acc-${item.id}`}
                            >
                              <Pin size={11} aria-hidden="true" /> Pinned
                            </span>
                          )}
                          {item.investmentStage && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{item.investmentStage}</span>
                          )}
                          {item.website && (
                            <a
                              href={item.website.startsWith("http") ? item.website : `https://${item.website}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-primary hover:underline flex items-center gap-1"
                              data-testid={`link-acc-website-${item.id}`}
                            >
                              <Globe size={14} aria-hidden="true" /> {item.website}
                              <span className="sr-only"> — {item.firmName} (opens in new tab)</span>
                            </a>
                          )}
                        </div>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                          {item.hqLocation && (
                            <div className="flex items-start gap-2 text-sm">
                              <MapPin size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">HQ Location</span>
                                <span className="text-foreground">{item.hqLocation}</span>
                              </div>
                            </div>
                          )}
                          {item.investmentStage && (
                            <div className="flex items-start gap-2 text-sm">
                              <TrendingUp size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">Investment Stage</span>
                                <span className="text-foreground">{item.investmentStage}</span>
                              </div>
                            </div>
                          )}
                          {item.investmentFocus && (
                            <div className="flex items-start gap-2 text-sm">
                              <Target size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">Investment Focus</span>
                                <span className="text-foreground">{item.investmentFocus}</span>
                              </div>
                            </div>
                          )}
                          {item.recentAnnouncements && (
                            <div className="flex items-start gap-2 text-sm">
                              <Megaphone size={14} className="text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                              <div>
                                <span className="text-xs text-muted-foreground block">Notes</span>
                                <span className="text-foreground whitespace-pre-wrap">{item.recentAnnouncements}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {(isOwner || isSiteOwner) && (
                        <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                          {isSiteOwner && isPinned && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Move ${item.firmName} up in pinned order`}
                                disabled={isFirstPin || reorderMutation.isPending}
                                onClick={() => movePin(item.id, -1)}
                                data-testid={`button-pin-up-acc-${item.id}`}
                              >
                                <ArrowUp size={14} aria-hidden="true" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Move ${item.firmName} down in pinned order`}
                                disabled={isLastPin || reorderMutation.isPending}
                                onClick={() => movePin(item.id, 1)}
                                data-testid={`button-pin-down-acc-${item.id}`}
                              >
                                <ArrowDown size={14} aria-hidden="true" />
                              </Button>
                            </>
                          )}
                          {isSiteOwner && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={isPinned ? `Unpin ${item.firmName}` : `Pin ${item.firmName} to top`}
                              disabled={pinMutation.isPending}
                              onClick={() => pinMutation.mutate({ id: item.id, pinned: !isPinned })}
                              data-testid={`button-pin-toggle-acc-${item.id}`}
                            >
                              {isPinned
                                ? <PinOff size={14} aria-hidden="true" />
                                : <Pin size={14} aria-hidden="true" />}
                            </Button>
                          )}
                          {isOwner && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Edit ${item.firmName}`}
                                onClick={() => startEdit(item)}
                                data-testid={`button-edit-acc-${item.id}`}
                              >
                                <Pencil size={14} aria-hidden="true" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400"
                                aria-label={`Delete ${item.firmName}`}
                                onClick={() => { if (confirm(`Delete ${item.firmName}?`)) deleteMutation.mutate(item.id); }}
                                data-testid={`button-delete-acc-${item.id}`}
                              >
                                <Trash2 size={14} aria-hidden="true" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                  );
                })}
              </div>
            ) : items && items.length > 0 ? (
              <div className="text-center py-16 bg-card border border-white/5 rounded-2xl">
                <Search size={32} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
                <h3 className="text-lg font-semibold mb-2">No results found</h3>
                <p className="text-muted-foreground text-sm">Try adjusting your search or filters.</p>
              </div>
            ) : (
              <div className="text-center py-20 bg-card border border-white/5 rounded-2xl">
                <Rocket size={40} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
                <h3 className="text-xl font-display font-semibold mb-3">No programs listed yet</h3>
                <p className="text-muted-foreground">The accelerator & incubator directory is being built. Check back soon.</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
